import {
  Component,
  Element,
  Event,
  EventEmitter,
  Host,
  Listen,
  Prop,
  State,
  h,
} from '@stencil/core';
import { HTMLStencilElement } from '@stencil/core/internal';
import { Unsubscribe, store } from '@stencil/redux';
import { SfcInputCustomEvent } from '@sfc/components';
import { IValidationResult, VALIDITY_STATE } from '@sfc/types';
import { Language, SigningMethod, SourceName } from '../../enums/const-enums';
import { RootState, initialStore } from '../../redux/store';
import { getActionType, handleBlur, handleEnterKey } from '../../utils/utils';
import {
  RassCodeSubmitTranslations,
  rassCodeSubmitTranslations,
} from './i18n/rass-code-submit.i18n';

@Component({
  tag: 'rass-code-submit',
  styleUrl: 'rass-code-submit.scss',
  shadow: false,
})
export class RassCodeSubmit {
  @Element() element!: HTMLStencilElement;
  @State() assetsPath: string;
  @State() language: Language;
  @State() sourceName: SourceName;
  @State() activeSignMethod:
    | SigningMethod.OOB
    | SigningMethod.RABO_SCANNER
    | SigningMethod.RANDOM_READER;
  @Prop() isDisabled = false;
  @State() code = '';
  @State() isValidCodeFormat = false;
  @State() showInvalidCodeMessage = false;
  regexMap = {
    [SigningMethod.OOB]: /^[0-9]{6}$/,
    [SigningMethod.RABO_SCANNER]: /^[0-9]{8}$/,
    [SigningMethod.RANDOM_READER]: /^[0-9]{8}$/,
  };
  maxLengthMap = {
    [SigningMethod.OOB]: 6,
    [SigningMethod.RABO_SCANNER]: 8,
    [SigningMethod.RANDOM_READER]: 8,
  };
  @Event({ eventName: 'rass:code:submit' })
  submitCodeEvent: EventEmitter<string>;
  unsubscribe!: Unsubscribe;
  signCodeInputElement!: HTMLSfcInputElement;

  componentWillLoad(): void {
    store.setStore(initialStore);
    this.unsubscribe = store.mapStateToProps(this, (state: RootState) => {
      const {
        config: { assetsPath, language, sourceName },
        methods: { activeSignMethod },
      } = state;
      return { assetsPath, language, sourceName, activeSignMethod };
    });
  }

  @Listen('code:reset', { target: 'window' })
  resetCodeInput(): void {
    this.code = '';
    this.isValidCodeFormat = false;
    this.showInvalidCodeMessage = false;
  }

  handleChangeCode(event: SfcInputCustomEvent<unknown>): void {
    this.code = (event.target as unknown as HTMLInputElement).value;
    this.isValidCodeFormat = false;

    if (this.code.match(this.regexMap[this.activeSignMethod])) {
      this.isValidCodeFormat = true;
    } else if (this.code.length === 8) {
      this.showInvalidCodeMessage = !this.isValidCodeFormat;
    }
  }

  doCodeSubmit(): void {
    this.submitCodeEvent.emit(this.code);
  }

  get translations(): RassCodeSubmitTranslations {
    return (
      rassCodeSubmitTranslations[this.language] ||
      rassCodeSubmitTranslations[Language.EN]
    );
  }

  addValidator(): void {
    this.signCodeInputElement
      .addValidator((): IValidationResult => {
        return this.showInvalidCodeMessage ||
          (!this.isValidCodeFormat && this.code.length === 8)
          ? {
              message: this.translations.input.error[this.activeSignMethod],
              type: VALIDITY_STATE.CUSTOM_ERROR,
            }
          : {};
      })
      .catch(() => console.warn('Unable to validate sign code.'));
  }

  handleInputBlur(): void {
    this.showInvalidCodeMessage = handleBlur(this.code, this.isValidCodeFormat);
    this.inputValidate();
  }

  handleInputKeyDown(event: SfcInputCustomEvent<KeyboardEvent>): void {
    this.showInvalidCodeMessage = handleEnterKey(
      event.detail,
      this.isValidCodeFormat,
      'rass-action-proceed',
    );
    this.inputValidate();
  }

  inputValidate(): void {
    this.signCodeInputElement
      .validate()
      .catch(() => console.warn('Unable to validate sign code.'));
  }

  render(): void {
    return (
      <Host class="section-code-submit">
        <style>@import url({`'${this.assetsPath}css/sfc-style.css'`});</style>
        <div class="section-form" role="form">
          <div class="sfc-row sfc-justify-content-center">
            <div class="input-div sfc-col-xs-12 sfc-col-sm-6">
              <sfc-input
                label={this.translations.input.label[getActionType()]}
                inputmode="numeric"
                max-length={this.maxLengthMap[this.activeSignMethod]}
                name="sign_code"
                id="sign_code"
                value={this.code}
                disabled={this.isDisabled}
                ref={(el: HTMLSfcInputElement): HTMLSfcInputElement =>
                  (this.signCodeInputElement = el)
                }
                onSfcInput={(event): void => this.handleChangeCode(event)}
                onSfcBlur={(): void => this.handleInputBlur()}
                onSfcKeyDown={(event): void => this.handleInputKeyDown(event)}
                onSfcReady={(): void => this.addValidator()}
              ></sfc-input>
            </div>
            <div class="submit-button-div sfc-col-xs-12 sfc-col-sm-6">
              <sfc-button
                id="rass-action-proceed"
                disabled={this.isDisabled || !this.isValidCodeFormat}
                onSfcClick={(): void => this.doCodeSubmit()}
              >
                {this.translations.button.label[this.sourceName]}
              </sfc-button>
              <div class="sfc-margin-block-end-100">
                <rass-change-account />
              </div>
            </div>
          </div>
        </div>
      </Host>
    ) as void;
  }
}
